/**
 * Frontend routing helpers — thin wrappers around the shared RPA vendor
 * registry so the form layer can reason about routing using the same
 * rules as the backend executor.
 *
 * Use `rpaVendorForForm({ payerName, tradingPartnerServiceId })` when
 * you need the vendor config (e.g. for display labels); use the boolean
 * `isRpaPayer` helper when you only need a yes/no for conditional UI.
 */

import {
  resolveRpaVendor,
  type RpaVendorConfig,
} from '../../shared/rpaRegistry'
import { DEFAULT_POLICY_TYPE, VISION_POLICY_TYPE } from '../../shared/policyType'

/**
 * Resolve the RPA vendor for a form's payer selection. Prefers the
 * deterministic TP-ID sentinel when available; falls back to the fuzzy
 * payer-name match. Returns `null` when the payer should route to Stedi.
 */
export function rpaVendorForForm(form: {
  payerName?: string
  tradingPartnerServiceId?: string
}): RpaVendorConfig | null {
  return resolveRpaVendor({
    tradingPartnerServiceId: form.tradingPartnerServiceId,
    payerName: form.payerName,
  })
}

/** Convenience boolean for conditional rendering (e.g. "hide Member ID"). */
export function isRpaPayer(form: {
  payerName?: string
  tradingPartnerServiceId?: string
}): boolean {
  return rpaVendorForForm(form) !== null
}

/**
 * Label a row's coverage from the payer it resolved to.
 *
 * Routing already knows this: a payer that resolves to an RPA vision vendor
 * (VSP / EyeMed) IS the vision coverage, and everything else is medical. So the
 * label is read off the resolved route rather than guessed from the payer's
 * name — no separate list to keep in sync, and a staff correction to the payer
 * moves the label with it.
 *
 * Deterministic, and shown in the review grid as a suggestion staff can change.
 */
export function coverageTypeForPayer(form: {
  payerName?: string
  tradingPartnerServiceId?: string
}): string {
  return isRpaPayer(form) ? VISION_POLICY_TYPE : DEFAULT_POLICY_TYPE
}
